import { createContext, useContext, useState, useCallback } from 'react';
import { useLoading } from '@/utils/LoadingUtil';
import {
  UploadProductFile,
  downloadBtsProductsFile,
  createSchoolProposal,
  createSchoolProposalInfo,
  updateSchoolProposalInfo,
  getSalesReps as fetchSalesReps,
  getBtsProducts,
  addManualProduct,
  updateManualProduct, // <-- import
  uploadProductFileByClass, // <-- import
  uploadSchoolProposalCsvFile,
  getBtsProductsByClass, // <-- import
  deleteClassProduct,
  getSchoolProposals,
  deleteSchoolProposal, // <-- add this import'
  exportBTSExcel,
  deleteMultipleProductByClass,
  exportParentsInstruction,
  exportPdf,
  exportBtsShReport,
  kitImportTemplateReport,
  getClassListForProposal,
  saveCategoryCode,
  exportContractExportReport,
  updateProposalStatus,
  updateClassStudentCount,
} from '@/services/BtsService';
import {
  createClass,
  getClasses,
  updateClass,
  deleteClass,
} from '@/services/ClassService';
import useBtsSchoolModel from '@/model/bts/BtsSchool.model';
import btsProductModel from '@/model/bts/BtsProduct.model';
import { ConfirmAlert, showAlert } from '../../components'; // <-- add this import if not present

const BtsContext = createContext({
  btsData: [],
  setBtsData: () => {},
  fetchBtsData: () => {},
  clearBtsData: () => {},
  uploadBtsProductFileByClass: () => {},
  downloadBtsProductFile: () => {},
  createSchoolProposal: () => {},
  createBtsSchoolProposalInfo: () => {},
  updateBtsSchoolProposalInfo: () => {},
  btsTableData: [],
  setBtsTableData: () => {},
  totalElements: 0,
  setTotalElements: () => {},
  currentPage: 0,
  setCurrentPage: () => {},
  pageSize: 10,
  setPageSize: () => {},
  searchQuery: '',
  setSearchQuery: () => {},
  sortBy: '',
  setSortBy: () => {},
  sortDir: 'desc',
  setSortDir: () => {},
  getAllBtsProposals: () => {},
  btsSchool: {},
  setBtsSchool: () => {},
  initialState: {},
});

export const useBts = () => {
  return useContext(BtsContext);
};

const BtsContextProvider = ({ children }) => {
  const { isLoading, startLoading, stopLoading } = useLoading();
  const { btsSchool, setBtsSchool, resetBtsSchool, initialState } =
    useBtsSchoolModel();

  const {
    btsProduct,
    setBtsProduct,
    resetBtsProduct,
    initialState: btsProductInitialState,
  } = btsProductModel();
  const [error, setError] = useState(null);
  const [salesReps, setSalesReps] = useState([]);
  const [classes, setClasses] = useState([]);
  const [btsProductsData, setBtsProductsData] = useState([]);
  const [categoryCodes, setCategoryCodes] = useState({});
  const [btsClassProdData, setBtsClassProdData] = useState(['No Data Found']);

  // BTS Table state (like customer context)
  const [btsTableData, setBtsTableData] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  const uploadBtsProductFile = async (file) => {
    startLoading();
    try {
      await UploadProductFile(file);
    } catch (error) {
      setError(error.message || 'An error occurred');
    } finally {
      stopLoading();
    }
  };

  const downloadBtsProductFile = async () => {
    startLoading();
    try {
      await downloadBtsProductsFile();
    } catch (error) {
      setError(error.message || 'An error occurred');
    } finally {
      stopLoading();
    }
  };

  const createBtsSchoolProposal = useCallback(
    async (btsSchool, e) => {
      if (e) e.preventDefault();
      startLoading();
      setError(null);
      try {
        // Prepare proposal and logoFile for API
        const proposal = { ...btsSchool.proposal };
        // Sanitize data
        if (proposal.status === '') proposal.status = null;
        if (proposal.salesRep && proposal.salesRep.id === 0) proposal.salesRep = null;

        // Accept logo from either btsSchool.logo or btsSchool.proposal.logoFile
        const logoFile = btsSchool.logo || btsSchool.proposal.logoFile || null;
        // Use Blob to set Content-Type: application/json for proposal part
        const formData = new FormData();
        formData.append(
          'proposal',
          new Blob([JSON.stringify(proposal)], { type: 'application/json' }),
        );
        if (logoFile) {
          formData.append('logo', logoFile);
        } else {
          formData.append('logo', '');
        }
        const resp = await createSchoolProposal(formData);
        return resp;
      } catch (err) {
        setError(err.response?.data || err.message || 'An error occurred');
        throw err;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading],
  );

  const createBtsSchoolProposalInfo = useCallback(
    async (btsSchool, e) => {
      if (e) e.preventDefault();
      startLoading();
      setError(null);
      try {
        const proposal = { ...btsSchool.proposal };
        if (proposal.salesRep && proposal.salesRep.id === 0) proposal.salesRep = null;

        const logoFile = btsSchool.logo || btsSchool.proposal.logoFile || null;
        const formData = new FormData();
        formData.append(
          'proposalInfo',
          new Blob([JSON.stringify(proposal)], { type: 'application/json' }),
        );
        if (logoFile) {
          formData.append('logo', logoFile);
        } else {
          formData.append('logo', '');
        }
        const resp = await createSchoolProposalInfo(formData);
        return resp;
      } catch (err) {
        setError(err.response?.data || err.message || 'An error occurred');
        throw err;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading],
  );

  const updateBtsSchoolProposal = useCallback(
    async (proposal, id, e) => {
      if (e) e.preventDefault();
      startLoading();
      setError(null);
      try {
        // Prepare proposal and logoFile for API (same as create)
        const proposalPayload = { ...proposal };
        if (proposalPayload.status === '') proposalPayload.status = null;
        if (proposalPayload.salesRep && proposalPayload.salesRep.id === 0) proposalPayload.salesRep = null;

        const logoFile = proposal.logoFile || null;
        const formData = new FormData();
        formData.append(
          'proposal',
          new Blob([JSON.stringify(proposalPayload)], { type: 'application/json' }),
        );
        if (logoFile) {
          formData.append('logo', logoFile);
        } else {
          formData.append('logo', '');
        }
        const { updateSchoolProposal } = await import('@/services/BtsService');
        const resp = await updateSchoolProposal(formData, id);
        return resp;
      } catch (err) {
        setError(err.response?.data || err.message || 'An error occurred');
        throw err;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading],
  );

  const updateBtsSchoolProposalInfo = useCallback(
    async (proposal, id, e) => {
      if (e) e.preventDefault();
      startLoading();
      setError(null);
      try {
        const proposalPayload = { ...proposal };
        if (proposalPayload.salesRep && proposalPayload.salesRep.id === 0) proposalPayload.salesRep = null;

        const logoFile = proposal.logoFile || null;
        const formData = new FormData();
        formData.append(
          'proposalInfo',
          new Blob([JSON.stringify(proposalPayload)], { type: 'application/json' }),
        );
        if (logoFile) {
          formData.append('logo', logoFile);
        } else {
          formData.append('logo', '');
        }
        const resp = await updateSchoolProposalInfo(formData, id);
        return resp;
      } catch (err) {
        setError(err.response?.data || err.message || 'An error occurred');
        throw err;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading],
  );

  const getSalesReps = useCallback(async () => {
    startLoading();
    try {
      const reps = await fetchSalesReps();
      setSalesReps(reps);
      return reps;
    } catch (error) {
      setError(error?.response?.data || error?.message || 'An error occurred');
      setSalesReps([]);
      return [];
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading]);

  // Class API integration
  const fetchClasses = useCallback(async () => {
    startLoading();
    try {
      const data = await getClasses();
      setClasses(data);
      return data;
    } catch (error) {
      setError(error?.response?.data || error?.message || 'An error occurred');
      setClasses([]);
      return [];
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading]);

  const createClassHandler = useCallback(
    async (classData) => {
      startLoading();
      try {
        const data = await createClass(classData);
        await fetchClasses();
        return data;
      } catch (error) {
        setError(
          error?.response?.data || error?.message || 'An error occurred',
        );
        throw error;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, fetchClasses],
  );

  const updateClassHandler = useCallback(
    async (id, classData) => {
      startLoading();
      try {
        const data = await updateClass(id, classData);
        await fetchClasses();
        return data;
      } catch (error) {
        setError(
          error?.response?.data || error?.message || 'An error occurred',
        );
        throw error;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, fetchClasses],
  );

  const deleteClassHandler = useCallback(
    async (id) => {
      startLoading();
      try {
        await deleteClass(id);
        await fetchClasses();
      } catch (error) {
        setError(
          error?.response?.data || error?.message || 'An error occurred',
        );
        throw error;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, fetchClasses],
  );

  // BTS Product search handler
  const fetchBtsProducts = useCallback(
    async (searchTerm) => {
      try {
        const products = await getBtsProducts(searchTerm);
        // Extract array from HAL-style API if present
        const arr = products?._embedded?.bTSProductDTOList
          ? products._embedded.bTSProductDTOList
          : Array.isArray(products)
            ? products
            : products?.data || products?.results || [];
        setBtsProductsData(arr);
        return arr;
      } catch (error) {
        setError(
          error?.response?.data || error?.message || 'An error occurred',
        );
        setBtsProductsData([]);
        return [];
      }
    },
    [startLoading, stopLoading],
  );

  // Handler to add manual BTS product
  const addManualProductHandler = useCallback(
    async (schoolProposalId, classId) => {
      startLoading();
      setError(null);
      try {
        // Pass btsProduct as product
        const resp = await addManualProduct(
          schoolProposalId,
          classId,
          btsProduct,
        );
        fetchBtsProductsByClass(schoolProposalId, classId);
        return resp;
      } catch (err) {
        setError(err.response?.data || err.message || 'An error occurred');
        throw err;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, btsProduct],
  );

  // Handler to update manual BTS product
  const updateManualProductHandler = useCallback(
    async (schoolProposalId, classId) => {
      startLoading();
      setError(null);
      try {
        // Pass btsProduct as product
        const resp = await updateManualProduct(
          schoolProposalId,
          classId,
          btsProduct,
        );
        fetchBtsProductsByClass(schoolProposalId, classId);
        return resp;
      } catch (err) {
        setError(err.response?.data || err.message || 'An error occurred');
        throw err;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, btsProduct],
  );

  // Handler to upload BTS product file (with schoolProposalId, classId)
  const uploadBtsProductFileByClassHandler = useCallback(
    async (file, schoolProposalId, classId) => {
      startLoading();
      setError(null);
      try {
        const resp = await uploadProductFileByClass(
          file,
          schoolProposalId,
          classId,
        );
        fetchBtsProductsByClass(schoolProposalId, classId);
        return resp;
      } catch (err) {
        setError(err.response?.data || err.message || 'An error occurred');
        throw err;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading],
  );

  const uploadSchoolProposalCsvFileHandler = useCallback(
    async (file, schoolProposalId) => {
      startLoading();
      setError(null);
      try {
        const resp = await uploadSchoolProposalCsvFile(
          file,
          schoolProposalId,
        );
        return resp;
      } catch (err) {
        setError(err.response?.data || err.message || 'An error occurred');
        throw err;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading],
  );

  // Handler to fetch BTS products by class and set state
  const fetchBtsProductsByClass = useCallback(
    async (schoolProposalId, classId) => {
      if (classId === undefined || classId === 0) return;
      startLoading();
      setError(null);
      try {
        const data = await getBtsProductsByClass(schoolProposalId, classId);
        setBtsClassProdData(data);
        return data;
      } catch (err) {
        setError(err.response?.data || err.message || 'An error occurred');
        setBtsClassProdData([]);
        return [];
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading],
  );

  // Handler to delete a BTS product from a class
  const deleteClassProductHandler = useCallback(
    async (schoolProposalId, classId, productId, isHeader) => {
      const result = await ConfirmAlert({ confirmBtnText: `Delete ${isHeader ? 'Header' : 'Product'}` });
      if (!result.isConfirmed) {
        showAlert('error', `${isHeader ? 'Header' : 'Product'} deletion cancelled`);
        return;
      }
      startLoading();
      setError(null);
      try {
        await deleteClassProduct(schoolProposalId, classId, productId, isHeader);
        await fetchBtsProductsByClass(schoolProposalId, classId);
      } catch (err) {
        setError(err.response?.data || err.message || 'An error occurred');
        throw err;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, fetchBtsProductsByClass],
  );

  // Handler to delete multiple BTS products by class
  const deleteMultipleProductByClassHandler = useCallback(
    async (schoolProposalId, classId, productIds) => {
      const result = await ConfirmAlert({
        confirmBtnText: 'Delete Selected Products',
      });
      if (!result.isConfirmed) {
        showAlert('error', 'Bulk product deletion cancelled');
        return;
      }
      startLoading();
      setError(null);
      try {
        // Send productIds as a simple array, not as { productIds }
        await deleteMultipleProductByClass(
          schoolProposalId,
          classId,
          productIds,
        );
        await fetchBtsProductsByClass(schoolProposalId, classId);
      } catch (err) {
        setError(err.response?.data || err.message || 'An error occurred');
        throw err;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, fetchBtsProductsByClass],
  );

  // Fetch all BTS proposals for table (like getAllCustomer)
  const getAllBtsProposals = useCallback(
    async (
      page = currentPage,
      size = pageSize,
      search = searchQuery,
      sortField = sortBy,
      sortDirection = sortDir,
    ) => {
      startLoading();
      try {
        const response = await getSchoolProposals(
          page,
          size,
          search,
          sortField,
          sortDirection,
        );
        setBtsTableData(
          response?._embedded?.schoolProposalDTOList || ['No Data Found'],
        );
        setTotalElements(response?.page?.totalElements || 0);
      } catch (error) {
        setBtsTableData([]);
        setTotalElements(0);
        setError(
          error?.response?.data || error?.message || 'An error occurred',
        );
      } finally {
        stopLoading();
      }
    },
    [
      currentPage,
      pageSize,
      searchQuery,
      sortBy,
      sortDir,
      startLoading,
      stopLoading,
    ],
  );

  // Delete BTS Proposal handler (like other delete API handlers)
  const deleteSchoolProposalHandler = useCallback(
    async (id) => {
      const result = await ConfirmAlert({ confirmBtnText: 'Delete Proposal' });
      if (!result.isConfirmed) {
        showAlert('error', 'Proposal deletion cancelled');
        return;
      }
      startLoading();
      setError(null);
      try {
        await deleteSchoolProposal(id);
        await getAllBtsProposals();
      } catch (err) {
        setError(err.response?.data || err.message || 'An error occurred');
        throw err;
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, getAllBtsProposals],
  );

  const exportBTSExcelReport = async (schoolProposalId) => {
    startLoading();
    try {
      await exportBTSExcel(schoolProposalId);
    } catch (error) {
      setError(error.message || 'An error occurred');
    } finally {
      stopLoading();
    }
  };
  const exportParentsInstructionAPI = async (schoolProposalId, classId, schoolName, className) => {
    startLoading();
    try {
      await exportParentsInstruction(schoolProposalId, classId, schoolName, className);
    } catch (error) {
      setError(error.message || 'An error occurred');
    } finally {
      stopLoading();
    }
  };
  const exportPdfAPI = async (schoolProposalId, classId) => {
    startLoading();
    try {
      await exportPdf(schoolProposalId, classId);
    } catch (error) {
      setError(error.message || 'An error occurred');
    } finally {
      stopLoading();
    }
  };

  const exportBtsShReportAPI = async (schoolProposalId, schoolName) => {
    startLoading();
    try {
      await exportBtsShReport(schoolProposalId, schoolName);
    } catch (error) {
      setError(error.message || 'An error occurred');
    } finally {
      stopLoading();
    }
  };

  const kitImportTemplateReportAPI = async (schoolProposalId, schoolName) => {
    startLoading();
    try {
      await kitImportTemplateReport(schoolProposalId, schoolName);
    } catch (error) {
      setError(error.message || 'An error occured');
    } finally {
      stopLoading();
    }
  };

  const getClassListForProposalAPI = async (schoolProposalId) => {
    startLoading();
    try {
      const data = await getClassListForProposal(schoolProposalId);
      return data;
    } catch (error) {
      setError(error.message || 'An error occured');
    } finally {
      stopLoading();
    }
  };

  const exportContractExportReportAPI = async (
    schoolProposalId,
    schoolName,
  ) => {
    startLoading();
    try {
      await exportContractExportReport(schoolProposalId, schoolName);
    } catch (error) {
      setError(error.message || 'An error occured');
    } finally {
      stopLoading();
    }
  };

  const saveCategoryCodeAPI = async (proposalId, data, schoolName) => {
    startLoading();
    try {
      await saveCategoryCode(proposalId, data, schoolName);
    } catch (error) {
      setError(error.message || 'An error occured');
    } finally {
      stopLoading();
    }
  };

  const updateProposalStatusHandler = useCallback(
    async (id, status) => {
      startLoading();
      try {
        await updateProposalStatus(id, status);
        await getAllBtsProposals();
      } catch (error) {
        setError(error.message || 'An error occurred');
      } finally {
        stopLoading();
      }
    },
    [startLoading, stopLoading, getAllBtsProposals],
  );

  return (
    <BtsContext.Provider
      value={{
        isLoading,
        error,
        setError,
        downloadBtsProductFile,
        btsSchool,
        setBtsSchool,
        resetBtsSchool,
        initialState,
        createBtsSchoolProposal,
        createBtsSchoolProposalInfo,
        updateBtsSchoolProposal,
        updateBtsSchoolProposalInfo,
        salesReps,
        getSalesReps,
        classes,
        fetchClasses,
        createClass: createClassHandler,
        updateClass: updateClassHandler,
        deleteClass: deleteClassHandler,
        btsProduct,
        setBtsProduct,
        resetBtsProduct,
        btsProductInitialState,
        btsProductsData,
        fetchBtsProducts,
        addManualProduct: addManualProductHandler,
        updateManualProduct: updateManualProductHandler,
        uploadBtsProductFile,
        uploadBtsProductFileByClassHandler,
        uploadSchoolProposalCsvFileHandler,
        deleteClassProduct: deleteClassProductHandler,
        deleteMultipleProductByClass: deleteMultipleProductByClassHandler,
        btsClassProdData,
        setBtsClassProdData,
        fetchBtsProductsByClass,
        btsTableData,
        setBtsTableData,
        totalElements,
        setTotalElements,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        searchQuery,
        setSearchQuery,
        sortBy,
        setSortBy,
        sortDir,
        setSortDir,
        getAllBtsProposals,
        deleteSchoolProposal: deleteSchoolProposalHandler,
        exportBTSExcelReport,
        exportParentsInstructionAPI,
        exportPdfAPI,
        exportBtsShReportAPI,
        kitImportTemplateReportAPI,
        getClassListForProposalAPI,
        saveCategoryCodeAPI,
        exportContractExportReportAPI,
        updateProposalStatus: updateProposalStatusHandler,
        updateClassStudentCount: updateClassStudentCount,
      }}>
      {children}
    </BtsContext.Provider>
  );
};

export default BtsContextProvider;
